import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollTopButton from "@/components/base/ScrollTopButton";
import EventCard from "@/components/feature/EventCard";
import JsonLd from "@/components/feature/JsonLd";
import useSeo from "@/hooks/useSeo";
import { categories, events } from "@/mocks/events";
import EventFilters from "@/pages/events/components/EventFilters";

const PAGE_SIZE = 6;
const siteUrl = "https://baoliaoqianxian.example.com/";

export default function Events() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const keyword = params.get("q") || "";
  const sort = params.get("sort") || "latest";
  const page = Math.max(1, Number(params.get("page") || "1"));

  useSeo({
    title: "热点总览 - 独家爆料事件时间线合集与最新进展",
    description:
      "独家爆料热点总览，按消费观察、网络热点、科技前线、娱乐圈、商业内幕、社会现场六个方向归集事件时间线，支持关键词搜索与热度排序，快速定位你想追踪的爆料进展。",
    keywords: "独家爆料最新,独家爆料追踪,热点事件时间线,爆料合集,事件反转后续",
    canonicalPath: "/events",
    ogType: "website",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const lower = keyword.toLowerCase();
    const list = events.filter((event) => {
      const catMatch = cat === "all" || event.categorySlug === cat;
      if (!catMatch) return false;
      if (!lower) return true;
      const haystack = `${event.title} ${event.summary} ${event.tags.join(" ")} ${event.category}`.toLowerCase();
      return haystack.includes(lower);
    });
    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === "nodes") sorted.sort((a, b) => b.timeline.length - a.timeline.length);
    else sorted.sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));
    return sorted;
  }, [cat, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const activeCategory = categories.find((item) => item.slug === cat);

  const buildHref = (targetPage: number) => {
    const next = new URLSearchParams(params);
    if (targetPage <= 1) next.delete("page");
    else next.set("page", String(targetPage));
    const qs = next.toString();
    return qs ? `/events?${qs}` : "/events";
  };

  return (
    <>
      <JsonLd
        id="events-collection"
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "热点总览 - 独家爆料事件时间线合集",
          url: `${siteUrl}events`,
          inLanguage: "zh-CN",
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
              { "@type": "ListItem", position: 2, name: "热点总览", item: `${siteUrl}events` },
            ],
          },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: filtered.map((event, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: event.title,
              url: `${siteUrl}events/${event.id}`,
            })),
          },
        }}
      />

      <section className="border-b border-background-200 paper-grain">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "热点总览" }]} />
          <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-foreground-950">
                {activeCategory ? `${activeCategory.name}专题` : "热点总览"}
              </h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
                {activeCategory
                  ? activeCategory.desc + "。以下事件均已整理为完整时间线，包含首发、更新、反转与后续节点。"
                  : "按主题浏览在跟的事件时间线，用关键词或排序快速定位你关心的爆料进展。"}
              </p>
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <div className="flex flex-col">
                <span className="font-heading text-2xl font-bold text-foreground-950">{events.length}</span>
                <span className="text-xs text-foreground-500">条已归档事件</span>
              </div>
              <span className="w-px h-10 bg-background-300" />
              <div className="flex flex-col">
                <span className="font-heading text-2xl font-bold text-foreground-950">{categories.length}</span>
                <span className="text-xs text-foreground-500">个跟踪方向</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-4">
          <EventFilters
            cat={cat}
            sort={sort}
            keyword={keyword}
            total={filtered.length}
            onCatChange={(value) => updateParam("cat", value === "all" ? "" : value)}
            onSortChange={(value) => updateParam("sort", value === "latest" ? "" : value)}
            onKeywordChange={(value) => updateParam("q", value)}
          />
        </div>
      </div>

      <section className="py-10 md:py-14">
        <div className="max-w-shell mx-auto px-4 md:px-6">
          {paged.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {paged.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-20 rounded-lg bg-background-100 border border-background-200">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center text-2xl">
                <i className="ri-search-eye-line" />
              </span>
              <p className="font-heading text-lg font-bold text-foreground-950">没有匹配的事件时间线</p>
              <p className="text-sm text-foreground-600">试着换个关键词，或清空筛选条件再看看。</p>
              <Link
                to="/events"
                className="mt-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部事件
              </Link>
            </div>
          )}

          {totalPages > 1 ? (
            <nav aria-label="分页" className="mt-10 flex items-center justify-center gap-2">
              <Link
                to={buildHref(Math.max(1, currentPage - 1))}
                aria-disabled={currentPage === 1}
                className={`w-10 h-10 rounded-md border flex items-center justify-center transition-colors ${
                  currentPage === 1
                    ? "border-background-200 text-foreground-300 pointer-events-none"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                }`}
              >
                <i className="ri-arrow-left-s-line" />
              </Link>
              {Array.from({ length: totalPages }).map((_, index) => {
                const target = index + 1;
                return (
                  <Link
                    key={target}
                    to={buildHref(target)}
                    className={`w-10 h-10 rounded-md border flex items-center justify-center text-sm transition-colors ${
                      target === currentPage
                        ? "bg-foreground-950 text-background-50 border-foreground-950"
                        : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                    }`}
                  >
                    {target}
                  </Link>
                );
              })}
              <Link
                to={buildHref(Math.min(totalPages, currentPage + 1))}
                aria-disabled={currentPage === totalPages}
                className={`w-10 h-10 rounded-md border flex items-center justify-center transition-colors ${
                  currentPage === totalPages
                    ? "border-background-200 text-foreground-300 pointer-events-none"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                }`}
              >
                <i className="ri-arrow-right-s-line" />
              </Link>
            </nav>
          ) : null}
        </div>
      </section>

      <ScrollTopButton />
    </>
  );
}